import { ElementFileUploadContextProvider } from "@/components/common/point-cloud-file-upload-context/element-file-upload-context";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectHasWritePermission } from "@/store/user-selectors";
import {
  cancelPpcCreators,
  cancelRegistrations,
  fetchDataInInterval,
  PreparePublishingDialog,
  progressStateFromServerSelector,
  RevisionChangeSummary,
  setShouldPreventRedirect,
  summarizeRevisionChanges,
  useCancelRevision,
  usePpcCreatorApiClientWithoutContext,
  useStagingAreaApiClient,
  useStagingAreaApiClientWithoutContext,
  useWorkflowContext,
  WorkflowProvider,
  workflowStateFromServerSelector,
} from "@faro-lotv/data-management";
import {
  FaroButton,
  FaroDialog,
  FaroMenu,
  FaroMenuItem,
  FaroText,
  ThreeDotsIcon,
  WarningIcon,
} from "@faro-lotv/flat-ui";
import { assert } from "@faro-lotv/foundation";
import {
  RegistrationRevision,
  RegistrationState,
  RevisionType,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { ButtonGroup } from "@mui/material";
import { Activity, useCallback, useMemo, useState, type JSX } from "react";
import { useIsDataPreparationReadOnly } from "../hooks/use-is-data-preparation-read-only";
import { loadRevisionDataThunk } from "../loading/load-revision-data-thunk";
import { selectRevisionRegistrationEdges } from "../store/revision/revision-edge-selectors";
import { selectRevisionEntities } from "../store/revision/revision-entity-selectors";
import { selectIsRevisionUserModified } from "../store/user-edits/data-preparation-user-edit-selectors";
import { PublishDraftDialog } from "../ui/data-preparation-dialogs/publish-draft-dialog";
import { RefineBeforePublishDialog } from "../ui/data-preparation-dialogs/refine-before-publish-dialog";
import {
  handle403AwareError,
  isRefinementSuggested,
  useSaveUserEdits,
} from "./refine-and-publish-utils";
import { RevisionChangesList } from "./revision-changes-list";

type PublishButtonProps = {
  /** The revision to publish. */
  revision: RegistrationRevision;
  /** Whether the button should be disabled. */
  disabledDueToError: boolean;
  /** Whether the button should show a warning on the left side. */
  showWithWarning: boolean;
};

/**
 * @param props The revision and publishing button state.
 * @returns A button to publish a revision, i.e. merge it into the main revision.
 */
export function PublishButton(props: PublishButtonProps): JSX.Element | null {
  const isReadOnly = useIsDataPreparationReadOnly();
  return isReadOnly ? null : <EditablePublishButton {...props} />;
}

function EditablePublishButton({
  revision,
  disabledDueToError,
  showWithWarning,
}: PublishButtonProps): JSX.Element | null {
  const [isPublishDialogOpen, setIsPublishDialogOpen] = useState(false);
  const [isRefineDialogOpen, setIsRefineDialogOpen] = useState(false);
  const [isPreparePublishingDialogOpen, setIsPreparePublishingDialogOpen] =
    useState<boolean>(false);
  const [isPublishLoading, setIsPublishLoading] = useState(false);
  const [savedRevision, setSavedRevision] =
    useState<RegistrationRevision>(revision);
  const { projectApiClient } = useApiClientContext();
  const stagingAreaApiClient = useStagingAreaApiClient();
  const progressState = useAppSelector(progressStateFromServerSelector);
  const workflowState = useAppSelector(workflowStateFromServerSelector);
  const { saveUserEdits } = useSaveUserEdits();
  const { handleErrorWithToast } = useErrorHandlers();
  const dispatch = useAppDispatch();
  const { getState } = useAppStore();

  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [areAdditionalOptionsOpen, setAreAdditionalOptionsOpen] =
    useState(false);
  const [isDiscardDraftDialogOpen, setIsDiscardDraftDialogOpen] =
    useState(false);

  const hasWritePermission = useAppSelector(selectHasWritePermission);

  const entities = useAppSelector(selectRevisionEntities);
  const edges = useAppSelector(selectRevisionRegistrationEdges);
  const revisionChanges = useMemo(
    () => summarizeRevisionChanges(entities, edges),
    [entities, edges],
  );
  const hasRevisionChanges =
    revisionChanges.sum > 0 &&
    revision.state !== RegistrationState.merged &&
    revision.state !== RegistrationState.canceled;

  const canPublishRevision =
    revision.state === RegistrationState.registered ||
    (revision.revisionType === RevisionType.draft &&
      revision.state !== RegistrationState.canceled);

  const isDisabled =
    !hasWritePermission ||
    !canPublishRevision ||
    disabledDueToError ||
    isPublishDialogOpen ||
    isRefineDialogOpen;

  const closeAdditionalOptions = useCallback(
    () => setAreAdditionalOptionsOpen(false),
    [],
  );
  const openDiscardDraftDialog = useCallback(() => {
    closeAdditionalOptions();
    setIsDiscardDraftDialogOpen(true);
  }, [closeAdditionalOptions]);
  const closeDiscardDraftDialog = useCallback(
    () => setIsDiscardDraftDialogOpen(false),
    [],
  );

  const closePublishDialog = useCallback(
    () => setIsPublishDialogOpen(false),
    [],
  );

  const closeRefineDialog = useCallback(() => setIsRefineDialogOpen(false), []);

  const continueWithoutRefinement = useCallback(() => {
    setIsRefineDialogOpen(false);
    setIsPublishDialogOpen(true);
  }, []);

  /**
   * Handle a click on the Publish button.
   *
   * Persists any pending user edits to the draft revision first, then evaluates
   * whether refinement is suggested based on the freshly fetched draft state
   * (avoiding any reliance on local front-end state). Opens the appropriate
   * follow-up dialog.
   */
  const onPublishClick = useCallback(async () => {
    setIsPublishLoading(true);
    try {
      let revisionToSave = revision;
      const isRevisionUserModified = selectIsRevisionUserModified(getState());
      if (isRevisionUserModified) {
        revisionToSave = await saveUserEdits(revision);
        const { revision: newRevision } = await dispatch(
          loadRevisionDataThunk({ projectApiClient }),
        );
        assert(
          newRevision,
          "Expected a revision to be returned after saving user edits",
        );
        revisionToSave = newRevision;
      }

      // Refresh the Data Management store used by the shared Publish button
      await fetchDataInInterval(
        dispatch,
        projectApiClient,
        stagingAreaApiClient,
      );

      // Get the new entities/edges from the store after refreshing the state
      const state = getState();
      const entities = selectRevisionEntities(state);
      const edges = selectRevisionRegistrationEdges(state);

      setSavedRevision(revisionToSave);
      if (isRefinementSuggested(entities, edges)) {
        setIsRefineDialogOpen(true);
      } else {
        setIsPublishDialogOpen(true);
      }
    } catch (error) {
      handle403AwareError(error, handleErrorWithToast);
    } finally {
      setIsPublishLoading(false);
    }
  }, [
    projectApiClient,
    stagingAreaApiClient,
    dispatch,
    getState,
    handleErrorWithToast,
    revision,
    saveUserEdits,
  ]);

  return (
    <WorkflowProvider
      workflowState={workflowState}
      progressState={progressState}
      hasAnyDraftChanges={revisionChanges.sum > 0}
    >
      <ButtonGroup sx={{ flexBasis: "content", flexShrink: 0 }}>
        <FaroButton
          aria-label="publish draft"
          variant="primary"
          disabled={isDisabled}
          isLoading={isPublishLoading}
          onClick={onPublishClick}
          icon={showWithWarning ? <WarningIcon /> : null}
        >
          Publish
        </FaroButton>

        <FaroButton
          aria-label="additional publish options"
          variant="primary"
          onClick={() => setAreAdditionalOptionsOpen(!areAdditionalOptionsOpen)}
          disabled={!hasRevisionChanges}
          ref={setDropdownButtonRef}
          sx={{ p: 0, width: "auto" }}
        >
          <ThreeDotsIcon />
        </FaroButton>
      </ButtonGroup>

      <FaroMenu
        open={areAdditionalOptionsOpen}
        onClose={closeAdditionalOptions}
        anchorEl={dropdownButtonRef}
        sx={{ mt: 1 }}
        // Prevent inner dialogs from closing when the menu is closed
        keepMounted
      >
        <ShowChangesMenuItem
          revisionChanges={revisionChanges}
          closeMenu={closeAdditionalOptions}
          onDiscardDraft={openDiscardDraftDialog}
          onPublish={onPublishClick}
        />

        <DiscardDraftMenuItem
          closeMenu={closeAdditionalOptions}
          onDiscardDraft={openDiscardDraftDialog}
        />
      </FaroMenu>

      <ElementFileUploadContextProvider>
        <DiscardDraftDialog
          revisionChanges={revisionChanges}
          open={isDiscardDraftDialogOpen}
          onClose={closeDiscardDraftDialog}
        />
      </ElementFileUploadContextProvider>

      <Activity mode={isPreparePublishingDialogOpen ? "visible" : "hidden"}>
        <PreparePublishingDialog />
      </Activity>

      <Activity
        mode={
          isPublishDialogOpen && !isPreparePublishingDialogOpen
            ? "visible"
            : "hidden"
        }
      >
        <PublishDraftDialog
          onCancel={closePublishDialog}
          afterPublish={closePublishDialog}
          hasRevisionChanges={hasRevisionChanges}
          setIsPreparePublishingDialogOpen={setIsPreparePublishingDialogOpen}
          open
        />
      </Activity>

      <Activity mode={isRefineDialogOpen ? "visible" : "hidden"}>
        <RefineBeforePublishDialog
          revision={savedRevision}
          onCancel={closeRefineDialog}
          onContinueWithoutRefinement={continueWithoutRefinement}
          open
        />
      </Activity>
    </WorkflowProvider>
  );
}

type UserChangesMenuItemProps = {
  /** A summary of the changes performed in the current revision. */
  revisionChanges: RevisionChangeSummary;
  /** Callback to close the containing menu. */
  closeMenu(): void;
};

type ShowChangesMenuItemProps = UserChangesMenuItemProps & {
  /** Callback to open the discard draft confirmation dialog. */
  onDiscardDraft(): void;
  /** Callback to start the publish flow. */
  onPublish(): void;
};

/** @returns Menu item showing the unpublished changes on the revision. */
function ShowChangesMenuItem({
  revisionChanges,
  closeMenu,
  onDiscardDraft,
  onPublish,
}: ShowChangesMenuItemProps): JSX.Element {
  const [isChangeDialogOpen, setIsChangeDialogOpen] = useState(false);

  return (
    <>
      <FaroMenuItem
        aria-label="show changes"
        label="Show changes"
        onClick={() => {
          closeMenu();
          setIsChangeDialogOpen(true);
        }}
      />
      <FaroDialog
        title="Unpublished changes"
        open={isChangeDialogOpen}
        cancelText="Close"
        secondaryText="Discard draft"
        confirmText="Publish"
        onClose={() => setIsChangeDialogOpen(false)}
        onCancel={() => setIsChangeDialogOpen(false)}
        onSecondary={() => {
          setIsChangeDialogOpen(false);
          onDiscardDraft();
        }}
        onConfirm={() => {
          setIsChangeDialogOpen(false);
          onPublish();
        }}
      >
        <RevisionChangesList revisionChanges={revisionChanges} />

        <FaroText variant="bodyM">
          Publish these changes to make them visible to all users.
        </FaroText>
      </FaroDialog>
    </>
  );
}

type DiscardDraftMenuItemProps = {
  /** Callback to close the containing menu. */
  closeMenu(): void;
  /** Callback to open the discard draft confirmation dialog. */
  onDiscardDraft(): void;
};

/** @returns Menu item showing the option to discard the draft revision. */
function DiscardDraftMenuItem({
  closeMenu,
  onDiscardDraft,
}: DiscardDraftMenuItemProps): JSX.Element {
  return (
    <FaroMenuItem
      aria-label="discard draft"
      label="Discard draft"
      onClick={() => {
        closeMenu();
        onDiscardDraft();
      }}
    />
  );
}

type DiscardDraftDialogProps = {
  /** A summary of the changes performed in the current revision. */
  revisionChanges: RevisionChangeSummary;
  /** Whether the dialog is open. */
  open: boolean;
  /** Callback to close the dialog. */
  onClose(): void;
};

/** @returns Dialog confirming that draft changes should be discarded. */
function DiscardDraftDialog({
  revisionChanges,
  open,
  onClose,
}: DiscardDraftDialogProps): JSX.Element {
  const [isDiscardingDraft, setIsDiscardingDraft] = useState(false);

  const dispatch = useAppDispatch();
  const { progressState } = useWorkflowContext();
  const cancelRevision = useCancelRevision();

  const { registrationApiClient, projectApiClient } = useApiClientContext();
  const stagingAreaApiClient = useStagingAreaApiClientWithoutContext({
    projectId: projectApiClient.projectId,
  });
  const ppcCreatorApiClient = usePpcCreatorApiClientWithoutContext({
    projectId: projectApiClient.projectId,
  });

  const { handleErrorWithToast } = useErrorHandlers();

  const onDiscardDraft = useCallback(async () => {
    if (isDiscardingDraft) {
      return;
    }
    try {
      setIsDiscardingDraft(true);
      const combinedCaptureTreeData = await fetchDataInInterval(
        dispatch,
        projectApiClient,
        stagingAreaApiClient,
      );
      try {
        dispatch(setShouldPreventRedirect(true));

        if (registrationApiClient) {
          await cancelRegistrations(
            registrationApiClient,
            progressState,
            /* shouldUseDiscard */ true,
          );
        }
        await cancelPpcCreators(
          ppcCreatorApiClient,
          progressState,
          /* shouldUseDiscard */ true,
        );

        if (combinedCaptureTreeData.openDraftRevision) {
          // This should be done last, otherwise ProjectAPI might automatically create a new draft revision
          // if there are still other open revisions.
          await cancelRevision(
            projectApiClient,
            combinedCaptureTreeData.openDraftRevision.id,
          );
          await dispatch(loadRevisionDataThunk({ projectApiClient }));
        }
      } catch (error) {
        handleErrorWithToast({
          error,
          title:
            "Failed to cancel the import process. Please reload the page and try again.",
        });
      }
    } catch (error) {
      handleErrorWithToast({
        error,
        title: "Failed to Discard Draft",
      });
    } finally {
      setIsDiscardingDraft(false);
      // Always close the dialog, even if there was no open draft revision to cancel.
      onClose();
      // Fetch the new state immediately instead of waiting until the next usePolling() happens.
      try {
        await fetchDataInInterval(
          dispatch,
          projectApiClient,
          stagingAreaApiClient,
        );
      } catch {
        /* Not essential. The UI will update a bit later. */
      }
    }
  }, [
    isDiscardingDraft,
    dispatch,
    projectApiClient,
    stagingAreaApiClient,
    registrationApiClient,
    ppcCreatorApiClient,
    progressState,
    cancelRevision,
    handleErrorWithToast,
    onClose,
  ]);

  return (
    <FaroDialog
      variant="danger"
      title="Discard Draft?"
      confirmText="Discard Draft"
      cancelText="No"
      open={open}
      onConfirm={onDiscardDraft}
      onClose={onClose}
      onCancel={onClose}
      isConfirmDisabled={isDiscardingDraft}
      showSpinner={isDiscardingDraft}
      showXButton
    >
      <FaroText variant="bodyM" sx={{ mb: 2 }}>
        You are about to discard the projects's draft version and revert these
        draft changes:
      </FaroText>

      <RevisionChangesList revisionChanges={revisionChanges} />
    </FaroDialog>
  );
}
